// Live-Kategoriezustand eines Artikels IM GAMBIO-SHOP (nicht der Vario-Spiegel).
//
// Warum live: `gambio.listings.vario_category_name/-_id` ist nur ein Spiegel der zuletzt über den
// Slot gesetzten Ziel-Kategorie. Wird die Kategorie im Vario-Verkaufskanal wieder ENTFERNT, bleibt
// der Spiegel stehen (die Schreib-RPC `gambio_set_listing_category` nutzt `coalesce`, kann also nur
// überschreiben, nie leeren) — die Anzeige behauptete dann eine Kategorie, die es nicht mehr gibt
// (User-Befund 2026-08-21). Die Wahrheit für „wo liegt der Artikel im Shop" ist der Shop selbst.
//
// Quelle: Edge Fn `gambio-product-categories` (`action:'list'`) → GET /products/{id}/links +
// GET /products/{id}.mainCategoryId, Namen sprachdynamisch aufgelöst. Die Fn schneidet ein
// Varianten-Suffix `-v…` ab, liest also für Variantenkinder die Kategorien des ELTERNprodukts —
// genau richtig, denn nur das Elternprodukt liegt in Kategorien.

/** Eine Kategorie-Verknüpfung des Produkts im Shop. */
export interface ShopCategory {
  id: number;
  name: string;
  isMain: boolean;
}

export type ShopCategoriesResult =
  /** Produkt existiert, `categories` kann leer sein (= im Shop keiner Kategorie zugeordnet). */
  | { kind: 'ok'; categories: ShopCategory[] }
  /** Kein Gambio-Mapping — der Artikel wurde noch nicht übertragen. */
  | { kind: 'not-listed' }
  /** Mapping vorhanden, Produkt im Shop aber gelöscht. */
  | { kind: 'gone' }
  /** Netz-/Server-Fehler: der Aufrufer soll auf den Vario-Wert zurückfallen und das kennzeichnen. */
  | { kind: 'error'; message: string };

export interface ShopCategoriesDeps {
  supabaseUrl: string;
  token: string;
  /** Injizierbar für Tests; Default `globalThis.fetch`. */
  fetchImpl?: typeof fetch;
}

/**
 * Liest die Kategorien eines Artikels live aus dem Gambio-Shop. Wirft NICHT — jeder Fehlerfall
 * kommt als Ergebnis-Variante zurück, damit die Anzeige entscheiden kann, was sie zeigt.
 */
export async function fetchShopCategories(
  deps: ShopCategoriesDeps,
  articleId: string,
  shopKey: string,
): Promise<ShopCategoriesResult> {
  const doFetch = deps.fetchImpl ?? fetch;
  try {
    const res = await doFetch(`${deps.supabaseUrl}/functions/v1/gambio-product-categories`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${deps.token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ articleId, shopKey, action: 'list' }),
    });
    const out = (await res.json().catch(() => ({}))) as {
      ok?: boolean;
      categories?: Array<{ id?: number; name?: string; isMain?: boolean }>;
      effectiveMainId?: number | null;
      error?: { code?: string; message?: string };
    };
    const code = out.error?.code ?? '';
    if (code === 'NOT_LISTED') return { kind: 'not-listed' };
    if (code === 'PRODUCT_GONE') return { kind: 'gone' };
    if (!res.ok || !out.ok) {
      return { kind: 'error', message: out.error?.message ?? `HTTP ${res.status}` };
    }
    const main = out.effectiveMainId ?? null;
    const categories: ShopCategory[] = (out.categories ?? [])
      .filter((c): c is { id: number; name?: string; isMain?: boolean } => typeof c.id === 'number')
      // `isMain` aus der Fn ist der EXPLIZITE Hauptkategorie-Flag; ist keine gesetzt, gewinnt
      // `effectiveMainId` (erster echter Link) — dieselbe Regel wie im Worker.
      // `isMain` der Fn ist bereits die EFFEKTIVE Hauptkategorie (explizite, sonst erster Link).
      // `effectiveMainId` wird nur als Rückfall mitgeprüft, falls die Fn das Flag mal nicht setzt.
      .map((c) => ({ id: c.id, name: c.name ?? `Kategorie ${c.id}`, isMain: c.isMain === true || c.id === main }));
    return { kind: 'ok', categories: sortMainFirst(categories) };
  } catch (e) {
    return { kind: 'error', message: e instanceof Error ? e.message : String(e) };
  }
}

/** Hauptkategorie zuerst, danach alphabetisch — stabile Anzeige-Reihenfolge. */
export function sortMainFirst(cats: ShopCategory[]): ShopCategory[] {
  return [...cats].sort((a, b) => {
    if (a.isMain !== b.isMain) return a.isMain ? -1 : 1;
    return a.name.localeCompare(b.name, 'de');
  });
}
